import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import MermaidDiagram from "../components/MermaidDiagram";
import { authFetch } from "../utils/auth";

type DocsDocument = {
  id: number;
  title: string;
  slug: string;
  summary: string;
  category: string;
  folder?: DocsFolder | null;
  fileUrl: string;
  updatedAt: string;
  readTime: string;
  tags: string[];
  isShared: boolean;
};

type DocsFolder = {
  id: number;
  name: string;
  slug: string;
  sort_order?: number;
};

type DocumentFolderGroup = {
  folder: DocsFolder;
  documents: DocsDocument[];
};

type MarkdownTable = {
  headers: string[];
  rows: string[][];
};

/** DRF 페이지네이션 `{ results: [] }` 또는 순수 배열 모두 처리 */
const normalizeDrfList = <T,>(payload: unknown): T[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && typeof payload === "object" && "results" in payload) {
    const candidate = (payload as { results?: unknown }).results;
    return Array.isArray(candidate) ? (candidate as T[]) : [];
  }

  return [];
};

const TOP_SECTION_KEY = "__top__";

const FolderChevronGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path fill="currentColor" d="M7 10l5 5 5-5H7z" />
  </svg>
);

const Page = styled.section`
  display: flex;
  flex-direction: column;
  gap: clamp(20px, 3vw, 30px);
`;

const PageHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 360px);
  gap: 20px;
  align-items: end;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const HeaderCopy = styled.div`
  min-width: 0;
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primarySoft};
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin-top: 16px;
  color: ${({ theme }) => theme.colors.text};
  font-size: clamp(30px, 4vw, 48px);
  line-height: 1.05;
  letter-spacing: -0.045em;
`;

const Description = styled.p`
  max-width: 760px;
  margin-top: 14px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: clamp(14px, 2vw, 16px);
  line-height: 1.75;
`;

const SearchPanel = styled.div`
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 22px;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.colors.shadow};
`;

const SearchLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 12px;
  font-weight: 900;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 44px;
  padding: 0 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 14px;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  font-size: 14px;
  font-weight: 700;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.primarySoft};
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 420px) minmax(0, 1fr);
  gap: clamp(18px, 2.5vw, 26px);
  align-items: start;
  min-width: 0;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 767px) {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }
`;

const LibraryPanel = styled.aside`
  position: sticky;
  top: 96px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  max-height: calc(100vh - 116px);

  @media (max-width: 1080px) {
    position: static;
    max-height: none;
  }

  @media (max-width: 767px) {
    width: 100%;
    max-width: 100%;
  }
`;

const LibrarySection = styled.section`
  padding: 18px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 26px;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.colors.shadow};
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 1080px) {
    overflow: hidden;
  }

  @media (max-width: 767px) {
    padding: 14px;
    border-radius: 22px;
  }
`;

const LibraryTitle = styled.h2`
  color: ${({ theme }) => theme.colors.text};
  font-size: 15px;
  letter-spacing: -0.02em;
`;

const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  overscroll-behavior: contain;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: ${({ theme }) => theme.colors.borderStrong};
  }

  @media (max-width: 1080px) {
    overflow-y: visible;
    padding-right: 0;
  }
`;

const DocumentGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FolderToggleButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 48px;
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 16px;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
  text-align: left;
  -webkit-tap-highlight-color: transparent;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

const FolderToggleText = styled.span`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
`;

const FolderTitle = styled.span`
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 8px;

  &::before {
    content: "";
    width: 14px;
    height: 11px;
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    border-radius: 3px;
    background: ${({ theme }) => theme.colors.primarySoft};
    box-shadow: inset 0 4px 0 ${({ theme }) => theme.colors.surface};
  }
`;

const FolderCount = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 11px;
  font-weight: 800;
`;

const ChevronIcon = styled.span<{ $open: boolean }>`
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: transform 0.2s ease;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});

  svg {
    width: 16px;
    height: 16px;
  }
`;

const GroupDocuments = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  /* styled-components의 display가 UA의 [hidden]{display:none}보다 우선해 접힘 무시되던 문제 수정 */
  &[hidden] {
    display: none !important;
  }

  @media (max-width: 1080px) {
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: 8px;
    scroll-snap-type: x mandatory;
    max-width: 100%;

    &::-webkit-scrollbar {
      height: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 999px;
      background: ${({ theme }) => theme.colors.borderStrong};
    }
  }
`;

const DocumentButton = styled.button<{ $active: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 12px;
  padding: 16px;
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary : theme.colors.border};
  border-radius: 22px;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : theme.colors.surfaceMuted};
  text-align: left;
  transition:
    border-color 0.2s ease,
    transform 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 1080px) {
    width: min(78vw, 340px);
    flex: 0 0 auto;
    scroll-snap-align: start;
  }

  @media (max-width: 420px) {
    width: calc(100vw - 76px);
    grid-template-columns: minmax(0, 1fr);
  }
`;

const DocumentIcon = styled.span`
  width: 34px;
  height: 42px;
  display: grid;
  place-items: center;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 12px;
  font-weight: 900;

  @media (max-width: 420px) {
    display: none;
  }
`;

const DocumentMeta = styled.div`
  min-width: 0;

  strong {
    display: block;
    overflow: hidden;
    color: ${({ theme }) => theme.colors.text};
    font-size: 13px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    display: block;
    margin-top: 5px;
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 12px;
    line-height: 1.4;
  }

  p {
    display: -webkit-box;
    margin-top: 10px;
    overflow: hidden;
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 12px;
    line-height: 1.55;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const DocumentMetaTop = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
`;

const CategoryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primarySoft};
  font-size: 11px;
  font-weight: 900;
`;

const ReadTime = styled.span`
  color: ${({ theme }) => theme.colors.textSoft};
  font-size: 12px;
  font-weight: 900;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 14px;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 11px;
  font-weight: 800;
`;

const ViewerPanel = styled.article`
  min-width: 0;
  max-width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 30px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.colors.shadow};
`;

const ViewerHeader = styled.header`
  min-width: 0;
  padding: clamp(22px, 3vw, 34px);
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background:
    radial-gradient(
      circle at 0% 0%,
      ${({ theme }) => theme.colors.primarySoft},
      transparent 34%
    ),
    ${({ theme }) => theme.colors.surface};
`;

const ViewerKicker = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 12px;
  font-weight: 900;
`;

const ViewerTitle = styled.h2`
  margin-top: 14px;
  color: ${({ theme }) => theme.colors.text};
  font-size: clamp(28px, 4vw, 42px);
  line-height: 1.08;
  letter-spacing: -0.045em;
  overflow-wrap: anywhere;
`;

const ViewerSummary = styled.p`
  max-width: 860px;
  margin-top: 14px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 15px;
  line-height: 1.75;
  overflow-wrap: anywhere;
`;

const MarkdownBody = styled.div`
  min-width: 0;
  max-width: 100%;
  padding: clamp(24px, 4vw, 46px);
  overflow-x: hidden;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 15px;
  line-height: 1.85;
  overflow-wrap: anywhere;
  word-break: keep-all;

  @media (max-width: 767px) {
    padding: 22px 16px;
    font-size: 14px;
    line-height: 1.78;
  }

  h1,
  h2,
  h3 {
    color: ${({ theme }) => theme.colors.text};
    letter-spacing: -0.035em;
  }

  h1 {
    margin: 0 0 24px;
    font-size: clamp(30px, 4vw, 44px);
    line-height: 1.08;
  }

  h2 {
    margin: 34px 0 14px;
    font-size: clamp(22px, 3vw, 30px);
    line-height: 1.2;
  }

  h3 {
    margin: 26px 0 10px;
    font-size: 19px;
  }

  p {
    margin: 12px 0;
  }

  ul,
  ol {
    margin: 12px 0 18px 22px;
  }

  li {
    margin: 6px 0;
  }

  strong {
    color: ${({ theme }) => theme.colors.text};
    font-weight: 900;
  }

  code {
    padding: 2px 7px;
    border-radius: 8px;
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primarySoft};
    font-family: "SFMono-Regular", Consolas, monospace;
    font-size: 0.92em;
  }

  pre {
    max-width: 100%;
    margin: 18px 0;
    padding: 18px;
    overflow-x: hidden;
    border-radius: 18px;
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.surfaceMuted};
    border: 1px solid ${({ theme }) => theme.colors.border};
    white-space: pre-wrap;
    overflow-wrap: anywhere;

    @media (max-width: 767px) {
      padding: 14px;
      font-size: 13px;
    }
  }

  pre code {
    padding: 0;
    background: transparent;
    color: inherit;
    white-space: inherit;
    overflow-wrap: inherit;
  }

  blockquote {
    margin: 18px 0;
    padding: 14px 16px;
    border-left: 4px solid ${({ theme }) => theme.colors.primary};
    border-radius: 14px;
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.primarySoft};
  }

  table {
    display: table;
    width: 100%;
    max-width: 100%;
    margin: 18px 0;
    table-layout: fixed;
    border-collapse: collapse;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 16px;
    white-space: normal;
  }

  th,
  td {
    padding: 12px 14px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    text-align: left;
    vertical-align: top;
    overflow-wrap: anywhere;
    word-break: keep-all;

    @media (max-width: 767px) {
      padding: 10px;
      font-size: 12px;
      line-height: 1.55;
    }
  }

  th {
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.surfaceMuted};
    font-size: 13px;
    font-weight: 900;
  }

  tr:last-child td {
    border-bottom: 0;
  }
`;

const EmptyState = styled.div`
  padding: 32px;
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: 24px;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.surface};
  text-align: center;
  font-size: 14px;
  font-weight: 800;
`;

const LoadingBox = styled.div`
  padding: 42px;
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  font-size: 14px;
  font-weight: 900;
`;

const splitTableRow = (line: string) => {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());
};

const renderInline = (text: string): React.ReactNode[] => {
  const segments = text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g).filter(Boolean);

  return segments.map((segment, index) => {
    if (segment.startsWith("`") && segment.endsWith("`")) {
      return <code key={`${segment}-${index}`}>{segment.slice(1, -1)}</code>;
    }

    if (segment.startsWith("**") && segment.endsWith("**")) {
      return (
        <strong key={`${segment}-${index}`}>{segment.slice(2, -2)}</strong>
      );
    }

    return (
      <React.Fragment key={`${segment}-${index}`}>{segment}</React.Fragment>
    );
  });
};

const renderMarkdown = (markdown: string) => {
  const lines = markdown.split("\n");
  const nodes: React.ReactNode[] = [];
  let index = 0;
  let nodeKey = 0;

  const pushParagraph = (line: string) => {
    nodes.push(<p key={`p-${nodeKey++}`}>{renderInline(line)}</p>);
  };

  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    if (trimmed.startsWith("```")) {
      const language = trimmed.slice(3).trim().toLowerCase();
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].trim().startsWith("```")) {
        codeLines.push(lines[index]);
        index += 1;
      }
      index += 1;
      const source = codeLines.join("\n");
      if (language === "mermaid") {
        nodes.push(
          <MermaidDiagram key={`mermaid-${nodeKey++}`} source={source} />,
        );
      } else {
        nodes.push(
          <pre key={`pre-${nodeKey++}`}>
            <code>{source}</code>
          </pre>,
        );
      }
      continue;
    }

    if (trimmed.startsWith("|") && lines[index + 1]?.includes("---")) {
      const headers = splitTableRow(trimmed);
      const rows: string[][] = [];
      index += 2;

      while (index < lines.length && lines[index].trim().startsWith("|")) {
        rows.push(splitTableRow(lines[index]));
        index += 1;
      }

      const table: MarkdownTable = { headers, rows };
      nodes.push(
        <table key={`table-${nodeKey++}`}>
          <thead>
            <tr>
              {table.headers.map((header) => (
                <th key={header}>{renderInline(header)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr key={`row-${rowIndex}`}>
                {row.map((cell, cellIndex) => (
                  <td key={`${cell}-${cellIndex}`}>{renderInline(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>,
      );
      continue;
    }

    if (trimmed.startsWith("# ")) {
      nodes.push(
        <h1 key={`h1-${nodeKey++}`}>
          {renderInline(trimmed.replace("# ", ""))}
        </h1>,
      );
      index += 1;
      continue;
    }

    if (trimmed.startsWith("## ")) {
      nodes.push(
        <h2 key={`h2-${nodeKey++}`}>
          {renderInline(trimmed.replace("## ", ""))}
        </h2>,
      );
      index += 1;
      continue;
    }

    if (trimmed.startsWith("### ")) {
      nodes.push(
        <h3 key={`h3-${nodeKey++}`}>
          {renderInline(trimmed.replace("### ", ""))}
        </h3>,
      );
      index += 1;
      continue;
    }

    if (trimmed.startsWith("> ")) {
      nodes.push(
        <blockquote key={`quote-${nodeKey++}`}>
          {renderInline(trimmed.replace("> ", ""))}
        </blockquote>,
      );
      index += 1;
      continue;
    }

    if (/^- /.test(trimmed)) {
      const items: string[] = [];
      while (index < lines.length && /^- /.test(lines[index].trim())) {
        items.push(lines[index].trim().replace("- ", ""));
        index += 1;
      }
      nodes.push(
        <ul key={`ul-${nodeKey++}`}>
          {items.map((item) => (
            <li key={item}>{renderInline(item)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    if (/^\d+\. /.test(trimmed)) {
      const items: string[] = [];
      while (index < lines.length && /^\d+\. /.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^\d+\. /, ""));
        index += 1;
      }
      nodes.push(
        <ol key={`ol-${nodeKey++}`}>
          {items.map((item) => (
            <li key={item}>{renderInline(item)}</li>
          ))}
        </ol>,
      );
      continue;
    }

    pushParagraph(trimmed);
    index += 1;
  }

  return nodes;
};

const DocsPage: React.FC = () => {
  const [documents, setDocuments] = useState<DocsDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(
    null,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [markdownLoading, setMarkdownLoading] = useState(false);
  const [markdownError, setMarkdownError] = useState<string | null>(null);
  const [sectionOpen, setSectionOpen] = useState<Record<string, boolean>>({});

  /** 기본은 모두 접힘. 열려 있으면 명시적으로 true */
  const isSectionOpen = (key: string) => sectionOpen[key] === true;

  const toggleSection = (key: string) => {
    setSectionOpen((prev) => ({
      ...prev,
      [key]: !isSectionOpen(key),
    }));
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchDocuments = async () => {
      try {
        setLoading(true);
        setError(null);

        const [docsResponse, foldersResponse] = await Promise.all([
          authFetch("/docs/", {
            signal: controller.signal,
          }),
          authFetch("/doc-folders/", {
            signal: controller.signal,
          }),
        ]);

        if (!docsResponse.ok) {
          throw new Error(`Docs load failed: ${docsResponse.status}`);
        }

        if (!foldersResponse.ok) {
          throw new Error(`Doc folders load failed: ${foldersResponse.status}`);
        }

        const foldersPayload = await foldersResponse.json();
        const folders = normalizeDrfList<DocsFolder>(foldersPayload);
        const folderNameById = new Map(
          folders.map((folder) => [folder.id, folder.name]),
        );

        const docsPayload = await docsResponse.json();
        const data = normalizeDrfList<DocsDocument>(docsPayload);
        setDocuments(
          data.map((document) => ({
            ...document,
            category:
              document.category ||
              (document.folder ? folderNameById.get(document.folder.id) : "") ||
              "General",
          })),
        );
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(
          err instanceof Error
            ? err.message
            : "문서 목록을 불러오지 못했습니다.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();

    return () => controller.abort();
  }, []);

  const filteredDocuments = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    if (!keyword) return documents;

    return documents.filter((document) => {
      const searchable = [
        document.title,
        document.summary,
        document.category,
        document.folder?.name ?? "",
        document.slug,
        ...document.tags,
      ]
        .join(" ")
        .toLowerCase();

      return searchable.includes(keyword);
    });
  }, [documents, searchTerm]);

  const topLevelDocuments = useMemo(() => {
    return filteredDocuments.filter((document) => !document.folder);
  }, [filteredDocuments]);

  const folderGroups = useMemo(() => {
    const groups = new Map<number, DocumentFolderGroup>();

    filteredDocuments.forEach((document) => {
      if (!document.folder) return;

      const currentGroup = groups.get(document.folder.id);
      if (currentGroup) {
        currentGroup.documents.push(document);
        return;
      }

      groups.set(document.folder.id, {
        folder: document.folder,
        documents: [document],
      });
    });

    return Array.from(groups.values()).sort((left, right) => {
      const leftOrder = left.folder.sort_order ?? 0;
      const rightOrder = right.folder.sort_order ?? 0;

      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }

      return left.folder.name.localeCompare(right.folder.name);
    });
  }, [filteredDocuments]);

  useEffect(() => {
    const keyword = searchTerm.trim();
    if (!keyword) return;

    setSectionOpen((prev) => {
      const next = { ...prev };
      if (topLevelDocuments.length > 0) {
        next[TOP_SECTION_KEY] = true;
      }
      folderGroups.forEach((group) => {
        if (group.documents.length > 0) {
          next[`folder-${group.folder.id}`] = true;
        }
      });
      return next;
    });
  }, [searchTerm, topLevelDocuments, folderGroups]);

  const selectedDocument = useMemo(() => {
    return (
      documents.find((document) => document.id === selectedDocumentId) ??
      filteredDocuments[0] ??
      documents[0] ??
      null
    );
  }, [documents, filteredDocuments, selectedDocumentId]);

  useEffect(() => {
    if (!documents.length || selectedDocumentId) return;
    setSelectedDocumentId(documents[0].id);
  }, [documents, selectedDocumentId]);

  useEffect(() => {
    if (!selectedDocument) return;

    const controller = new AbortController();

    const fetchMarkdown = async () => {
      try {
        setMarkdownLoading(true);
        setMarkdownError(null);

        const response = await fetch(selectedDocument.fileUrl, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Markdown load failed: ${response.status}`);
        }

        const text = await response.text();
        setMarkdown(text);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setMarkdownError(
          err instanceof Error ? err.message : "문서를 불러오지 못했습니다.",
        );
      } finally {
        setMarkdownLoading(false);
      }
    };

    fetchMarkdown();

    return () => controller.abort();
  }, [selectedDocument]);

  const introTitle = "Neural OS Docs";
  const introDescription =
    "공용 문서와 고객사 전용 운영 문서를 한 곳에서 확인하세요.";

  const renderDocumentButton = (document: DocsDocument) => (
    <DocumentButton
      key={document.id}
      type="button"
      $active={selectedDocument?.id === document.id}
      onClick={() => setSelectedDocumentId(document.id)}
    >
      <DocumentIcon>MD</DocumentIcon>
      <DocumentMeta>
        <DocumentMetaTop>
          <CategoryBadge>{document.category}</CategoryBadge>
          <ReadTime>{document.readTime}</ReadTime>
        </DocumentMetaTop>
        <strong>{document.title}</strong>
        <p>{document.summary}</p>
        <TagList>
          {document.tags.map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </TagList>
      </DocumentMeta>
    </DocumentButton>
  );

  return (
    <Page>
      <PageHeader>
        <HeaderCopy>
          <Eyebrow>Knowledge Base</Eyebrow>
          <Title>{introTitle}</Title>
          <Description>{introDescription}</Description>
        </HeaderCopy>
        <SearchPanel>
          <SearchLabel>
            문서 검색
            <SearchInput
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="제목, 태그, 파일명으로 검색"
            />
          </SearchLabel>
        </SearchPanel>
      </PageHeader>

      {loading && <LoadingBox>문서 목록을 불러오는 중입니다.</LoadingBox>}
      {error && (
        <EmptyState>문서 설정을 불러오지 못했습니다. {error}</EmptyState>
      )}

      {!loading && !error && (
        <Layout>
          <LibraryPanel>
            <LibrarySection>
              <LibraryTitle>문서 선택</LibraryTitle>
              <DocumentList>
                {topLevelDocuments.length > 0 && (
                  <DocumentGroup>
                    <FolderToggleButton
                      type="button"
                      id={`docs-trigger-${TOP_SECTION_KEY}`}
                      aria-expanded={isSectionOpen(TOP_SECTION_KEY)}
                      aria-controls="docs-panel-top"
                      onClick={() => toggleSection(TOP_SECTION_KEY)}
                    >
                      <FolderToggleText>
                        <FolderTitle>최상위 문서</FolderTitle>
                        <FolderCount>{topLevelDocuments.length}개</FolderCount>
                      </FolderToggleText>
                      <ChevronIcon $open={isSectionOpen(TOP_SECTION_KEY)}>
                        <FolderChevronGlyph />
                      </ChevronIcon>
                    </FolderToggleButton>
                    <GroupDocuments
                      id="docs-panel-top"
                      role="region"
                      aria-labelledby={`docs-trigger-${TOP_SECTION_KEY}`}
                      hidden={!isSectionOpen(TOP_SECTION_KEY)}
                    >
                      {topLevelDocuments.map(renderDocumentButton)}
                    </GroupDocuments>
                  </DocumentGroup>
                )}
                {folderGroups.map((group) => {
                  const sectionKey = `folder-${group.folder.id}`;
                  return (
                    <DocumentGroup key={group.folder.id}>
                      <FolderToggleButton
                        type="button"
                        id={`docs-trigger-${sectionKey}`}
                        aria-expanded={isSectionOpen(sectionKey)}
                        aria-controls={`docs-panel-${sectionKey}`}
                        onClick={() => toggleSection(sectionKey)}
                      >
                        <FolderToggleText>
                          <FolderTitle>{group.folder.name}</FolderTitle>
                          <FolderCount>{group.documents.length}개</FolderCount>
                        </FolderToggleText>
                        <ChevronIcon $open={isSectionOpen(sectionKey)}>
                          <FolderChevronGlyph />
                        </ChevronIcon>
                      </FolderToggleButton>
                      <GroupDocuments
                        id={`docs-panel-${sectionKey}`}
                        role="region"
                        aria-labelledby={`docs-trigger-${sectionKey}`}
                        hidden={!isSectionOpen(sectionKey)}
                      >
                        {group.documents.map(renderDocumentButton)}
                      </GroupDocuments>
                    </DocumentGroup>
                  );
                })}
              </DocumentList>
              {!filteredDocuments.length && (
                <EmptyState>검색된 문서가 없습니다.</EmptyState>
              )}
            </LibrarySection>
          </LibraryPanel>

          <div>
            {selectedDocument && (
              <ViewerPanel>
                <ViewerHeader>
                  <ViewerKicker>
                    <span>{selectedDocument.category}</span>
                    <span>·</span>
                    <span>{selectedDocument.updatedAt}</span>
                    <span>·</span>
                    <span>{selectedDocument.readTime}</span>
                  </ViewerKicker>
                  <ViewerTitle>{selectedDocument.title}</ViewerTitle>
                  <ViewerSummary>{selectedDocument.summary}</ViewerSummary>
                  <TagList>
                    {selectedDocument.tags.map((tag) => (
                      <Tag key={tag}>{tag}</Tag>
                    ))}
                  </TagList>
                </ViewerHeader>
                <MarkdownBody>
                  {markdownLoading && (
                    <LoadingBox>문서를 불러오는 중입니다.</LoadingBox>
                  )}
                  {markdownError && <EmptyState>{markdownError}</EmptyState>}
                  {!markdownLoading &&
                    !markdownError &&
                    renderMarkdown(markdown)}
                </MarkdownBody>
              </ViewerPanel>
            )}
          </div>
        </Layout>
      )}
    </Page>
  );
};

export default DocsPage;
